import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';

// ─── Print styles ─────────────────────────────────────────────────────────────
const PRINT_STYLES = `
@media print {
  @page { size: A4 landscape; margin: 16mm 12mm; }

  body * { visibility: hidden; }
  #overdue-detail-print, #overdue-detail-print * { visibility: visible; }

  #overdue-detail-print {
    position: absolute; left: 0; top: 0; width: 100%;
    background: #fff; padding: 0; margin: 0;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9pt; color: #111; line-height: 1.5;
  }

  .print-show { display: block !important; }
  .print-hide { display: none !important; }

  #overdue-detail-print * {
    box-shadow: none !important; border-radius: 0 !important;
    animation: none !important; transition: none !important;
  }
  #overdue-detail-print span, #overdue-detail-print p,
  #overdue-detail-print li, #overdue-detail-print div,
  #overdue-detail-print td, #overdue-detail-print th {
    color: #111 !important; background-color: transparent !important;
  }

  .print-doc-header { border-bottom: 1.5pt solid #333; padding-bottom: 6pt; margin-bottom: 10pt; }
  .print-doc-header .print-doc-title { display: flex; align-items: baseline; justify-content: space-between; }
  .print-doc-header h1 { font-size: 14pt; font-weight: bold; margin: 0; color: #111 !important; }
  .print-doc-header .print-doc-meta { font-size: 8pt; color: #444 !important; text-align: right; }
  .print-doc-header p { font-size: 9pt; margin: 3pt 0 0; color: #333 !important; }

  #overdue-detail-print .print-stats-grid {
    display: grid !important; grid-template-columns: repeat(2, 1fr) !important;
    gap: 0 !important; border: 0.75pt solid #aaa !important; margin: 10pt 0 !important;
    max-width: 260pt;
  }
  #overdue-detail-print .print-stats-grid > div {
    border-right: 0.75pt solid #aaa !important; padding: 4pt 8pt !important;
    text-align: center !important; background: transparent !important;
  }
  #overdue-detail-print .print-stats-grid > div:last-child { border-right: none !important; }

  #overdue-detail-print table { border-collapse: collapse !important; width: 100% !important; font-size: 8pt !important; }
  #overdue-detail-print th, #overdue-detail-print td {
    border: 0.4pt solid #999 !important; padding: 3pt 5pt !important;
    color: #111 !important; background: transparent !important;
  }
  #overdue-detail-print thead th { background: #eaeaea !important; font-weight: bold !important; }
  #overdue-detail-print tr { page-break-inside: avoid; break-inside: avoid; }

  .print-signoff {
    margin-top: 26pt; display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 24pt;
  }
  .print-signoff .print-sign-line {
    border-top: 0.75pt solid #333; margin-top: 22pt; padding-top: 3pt; font-size: 8pt; color: #444 !important;
  }
  .print-footnote { margin-top: 14pt; font-size: 7.5pt; color: #666 !important; }
}

@media screen {
  .print-show { display: none !important; }
}
`;

export default function NotCorrectedOverdueStaffDetail() {
  const { staffId } = useParams();

  const [staff, setStaff] = useState(null);
  const [thresholdDays, setThresholdDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);

  const preparedBy = AuthService.getUser()?.superAdmin?.staff?.name || 'System';
  const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;
        const data = accessRes.data || {};
        setCoordinateCourses(data.coordinateCourses || {});
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses', e);
      } finally {
        if (isMounted) setAccessLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const loadStaffDetail = useCallback(async () => {
    if (!accessLoaded || !staffId) return;

    let accessibleParam = 'all';
    if (!isSuperAdmin && !isManagement) {
      const numericIds = (Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [])
        .map((id) => Number(id))
        .filter((num) => Number.isFinite(num) && num > 0);

      if (numericIds.length === 0) {
        setStaff(null);
        setLoading(false);
        return;
      }
      accessibleParam = numericIds;
    }

    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/assignment-summary/overdue', {
        params: { accessibleCourses: accessibleParam, staffId },
      });
      const data = resp?.data || {};
      setStaff((data.staffCards && data.staffCards[0]) || null);
      setThresholdDays(data.overdueThresholdDays || 14);
      setGeneratedAt(data.generatedAt || new Date().toISOString());
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load lecturer detail');
    } finally {
      setLoading(false);
    }
  }, [accessLoaded, accessibleCourseIds, isSuperAdmin, isManagement, staffId]);

  useEffect(() => {
    loadStaffDetail();
  }, [loadStaffDetail]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <DashboardLayout title="Not Corrected Assignment Detail">
        <div id="overdue-detail-print" className="w-full">
          {loading ? (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600 print-hide">Loading…</div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 print-hide">{error}</div>
          ) : !staff ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600 print-hide">
              No pending assignments found for this lecturer.
            </div>
          ) : (
            <>
              {/* Print-only formal document header */}
              <div className="print-show">
                <div className="print-doc-header">
                  <div className="print-doc-title">
                    <h1>14 Days Passed – Not Corrected Assignment Report</h1>
                    <div className="print-doc-meta">
                      Generated: {generatedAt ? new Date(generatedAt).toLocaleString('en-GB') : '—'}
                      <br />
                      Prepared by: {preparedBy}
                    </div>
                  </div>
                  <p>
                    Lecturer: <strong>{staff.staffName}</strong>
                    {(staff.officeEmail || staff.gmail) ? ` · ${staff.officeEmail || staff.gmail}` : ''}
                  </p>
                </div>
                <div className="print-stats-grid">
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '11pt' }}>{staff.overdueCount}</div>
                    <div>{thresholdDays}+ days overdue</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '11pt' }}>{staff.pendingCount}</div>
                    <div>&lt; {thresholdDays} days pending</div>
                  </div>
                </div>
              </div>

              <div className="mb-6 flex flex-col gap-4 rounded-xl border border-red-200 bg-red-50 p-6 sm:flex-row sm:items-center sm:justify-between print-hide">
                <div className="flex items-center gap-4">
                  <img
                    src={getImageURL(staff.imageUrl)}
                    alt={staff.staffName}
                    className="h-16 w-16 rounded-full object-cover ring-2 ring-red-200"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{staff.staffName}</h2>
                    <p className="text-sm text-gray-600">{staff.officeEmail || staff.gmail || '—'}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div className="rounded-lg border border-red-300 bg-white px-4 py-2 text-center">
                    <div className="text-lg font-bold text-red-700">{staff.overdueCount}</div>
                    <div className="text-[11px] text-red-700/80">{thresholdDays}+ days</div>
                  </div>
                  <div className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-center">
                    <div className="text-lg font-bold text-amber-700">{staff.pendingCount}</div>
                    <div className="text-[11px] text-amber-700/80">&lt; {thresholdDays} days</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2z" />
                    </svg>
                    Print
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="w-full min-w-[1100px] text-left">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      <th className="px-5 py-2.5">Student</th>
                      <th className="px-5 py-2.5">Student ID</th>
                      <th className="px-5 py-2.5">Registration No.</th>
                      <th className="px-5 py-2.5">Subject</th>
                      <th className="px-5 py-2.5">Batch</th>
                      <th className="px-5 py-2.5">Course</th>
                      <th className="px-5 py-2.5 text-center">Type</th>
                      <th className="px-5 py-2.5 text-center">Since</th>
                      <th className="px-5 py-2.5 text-center">Days Pending</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {staff.items.map((item) => (
                      <tr key={`${item.assignmentType}-${item.assignmentId}`} className="hover:bg-gray-50">
                        <td className="max-w-[220px] truncate px-5 py-3 text-sm font-medium text-gray-800">
                          {item.studentName || '—'}
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-600">{item.studentId || '—'}</td>
                        <td className="px-5 py-3 text-xs text-gray-600">{item.registrationNumber || '—'}</td>
                        <td className="max-w-[200px] truncate px-5 py-3 text-sm text-gray-800">
                          {item.subjectShortName || item.subjectName || 'Unknown Subject'}
                        </td>
                        <td className="max-w-[200px] truncate px-5 py-3 text-xs text-gray-700">
                          {item.batchInternalName || item.batchLabel || item.batchName || '—'}
                        </td>
                        <td className="max-w-[180px] truncate px-5 py-3 text-xs text-gray-500">
                          {item.courseShortName || item.courseName || '—'}
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                            {item.assignmentType === 'topup' ? 'TOPUP' : 'HND'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-center text-xs text-gray-600">{fmtDate(item.marksUpdatedDate)}</td>
                        <td className="px-5 py-3 text-center">
                          <span className={`inline-block rounded-full px-2 py-1 text-xs font-bold ${
                            item.isOverdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {item.daysPending}d
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Print-only sign-off block */}
              <div className="print-show print-signoff">
                <div className="print-sign-line">Lecturer's Signature &amp; Date</div>
                <div className="print-sign-line">Head of Department – Signature &amp; Date</div>
              </div>
              <p className="print-show print-footnote">
                This is a system-generated report from the Kaaryaalaya academic management system. Figures reflect
                submissions still awaiting marks as of the generation time above.
              </p>
            </>
          )}
        </div>
      </DashboardLayout>
    </>
  );
}
